import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import ScrollReveal from "../components/ScrollReveal";
import { COMPANY } from "../lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We will be in touch shortly.");
      setDone(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Unable to send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="page-contact" className="pt-32 md:pt-40 bg-[#FCFCFA]">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="grid md:grid-cols-12 gap-10">
          <ScrollReveal className="md:col-span-8">
            <div className="flex items-center gap-4 mb-6">
              <span className="hairline-gold" />
              <span className="overline">Contact Us</span>
            </div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.02] text-[#1A1D20]">
              Let's build the future together.
            </h1>
            <p className="mt-8 text-[#5C636A] text-[16px] md:text-[18px] leading-relaxed max-w-2xl">
              Whether you're an investor, partner, supplier, journalist or
              neighbour — we'd love to hear from you. Reach out below and a
              member of our team will respond shortly.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white border-t border-[#E5E5DF]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20">
          {/* Contact info */}
          <div className="md:col-span-4">
            <p className="overline mb-6">Head Office</p>
            <ul className="space-y-6 text-[15px] text-[#1A1D20]">
              <li className="flex gap-4">
                <MapPin size={18} strokeWidth={1.5} className="text-[#D4AF37] mt-1 shrink-0" />
                <span className="leading-relaxed">{COMPANY.address}</span>
              </li>
              <li className="flex gap-4">
                <Phone size={18} strokeWidth={1.5} className="text-[#D4AF37] mt-1 shrink-0" />
                <a data-testid="contact-phone" href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="hover:text-[#D4AF37]">
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex gap-4">
                <Mail size={18} strokeWidth={1.5} className="text-[#D4AF37] mt-1 shrink-0" />
                <a data-testid="contact-email" href={`mailto:${COMPANY.email}`} className="hover:text-[#D4AF37]">
                  {COMPANY.email}
                </a>
              </li>
            </ul>

            <div className="mt-14 border-t border-[#E5E5DF] pt-8">
              <p className="overline mb-4">Office Hours</p>
              <p className="text-[14px] leading-relaxed text-[#5C636A]">
                Monday – Friday<br />
                08:00 – 17:00 CAT
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="md:col-span-8">
            {done ? (
              <div
                data-testid="contact-success"
                className="border border-[#D4AF37] p-10 bg-[#FCFCFA]"
              >
                <CheckCircle2 size={28} className="text-[#D4AF37]" strokeWidth={1.5} />
                <p className="mt-6 font-serif-display text-3xl text-[#1A1D20]">
                  Message received.
                </p>
                <p className="mt-3 text-[#5C636A] text-[15px] leading-relaxed">
                  Thank you for reaching out. A member of our team will
                  respond to your enquiry shortly.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="contact-reset"
                  className="mt-8 text-[12px] uppercase tracking-[0.2em] text-[#1A1D20] border-b border-[#D4AF37]"
                >
                  Send another message →
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="grid grid-cols-1 md:grid-cols-2 gap-8"
              >
                <Field label="Full Name" required>
                  <input
                    required
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    data-testid="contact-name"
                    className="w-full bg-transparent border-b border-[#1A1D20]/30 focus:border-[#D4AF37] outline-none py-3 text-[15px] placeholder-[#1A1D20]/40"
                    placeholder="Your name"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="contact-email-input"
                    className="w-full bg-transparent border-b border-[#1A1D20]/30 focus:border-[#D4AF37] outline-none py-3 text-[15px] placeholder-[#1A1D20]/40"
                    placeholder="you@domain.com"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="contact-phone-input"
                    className="w-full bg-transparent border-b border-[#1A1D20]/30 focus:border-[#D4AF37] outline-none py-3 text-[15px] placeholder-[#1A1D20]/40"
                    placeholder="+265 ..."
                  />
                </Field>
                <Field label="Subject" required>
                  <input
                    required
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    data-testid="contact-subject"
                    className="w-full bg-transparent border-b border-[#1A1D20]/30 focus:border-[#D4AF37] outline-none py-3 text-[15px] placeholder-[#1A1D20]/40"
                    placeholder="How can we help?"
                  />
                </Field>
                <Field label="Message" required className="md:col-span-2">
                  <textarea
                    required
                    name="message"
                    rows={6}
                    minLength={5}
                    value={form.message}
                    onChange={onChange}
                    data-testid="contact-message"
                    className="w-full bg-transparent border-b border-[#1A1D20]/30 focus:border-[#D4AF37] outline-none py-3 text-[15px] placeholder-[#1A1D20]/40 resize-none"
                    placeholder="Tell us more..."
                  />
                </Field>
                <div className="md:col-span-2 pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="inline-flex items-center bg-[#1A1D20] text-white hover:bg-[#D4AF37] hover:text-[#1A1D20] transition-colors duration-300 px-10 py-4 uppercase tracking-[0.18em] text-[12px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({ label, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] text-[#5C636A] mb-2">
        {label}{required && <span className="text-[#D4AF37]"> *</span>}
      </span>
      {children}
    </label>
  );
}
